import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  AppBar, Toolbar, Typography, Box, CssBaseline, ThemeProvider, Paper,
  CircularProgress, Alert, Button, Tabs, Tab, IconButton, Modal,
  TextField, Snackbar, Chip
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import MapIcon from '@mui/icons-material/Map';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import html2canvas from 'html2canvas';
import axios from 'axios';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import SharedViewPage from './pages/SharedViewPage';
import LogoutIcon from '@mui/icons-material/Logout';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';

import MapComponent from './components/MapComponent';
import FilterPanel from './components/FilterPanel';
import SpaceDetailsPanel from './components/SpaceDetailsPanel';
import brandTheme from './theme';
import Logo from './assets/logo_bms.png';
import StatsDashboard from './components/StatsDashboard';
import ShareLinksManager from './components/ShareLinksManager';
import ProposalBuilder from './components/ProposalBuilder';
import BillboardFormModal from './components/BillboardFormModal';
import PublicPortal from './pages/PublicPortal';
import LeadsManager from './components/LeadsManager';
import FeedbackIcon from '@mui/icons-material/Feedback';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import InventoryGantt from './components/InventoryGantt';
import AsignacionesManager from './components/AsignacionesManager';
import KpiDashboard from './components/KpiDashboard';
import InventoryManager from './components/InventoryManager';
import UsersManager from './components/UsersManager';
import LogsManager from './components/LogsManager';
import NotificationsMenu from './components/layout/NotificationsMenu';

import { API_BASE_URL, SHARE_API, PDF_GENERATOR_API_URL, API_ROOT } from './apiConfig';

function App() {
  const initialFilters = { codigo: '', tipo_espacio: [], tamano: [], zona: [], provincia: [], estado: [], precio_min: '', precio_max: '' };

  const [espacios, setEspacios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [filterOptions, setFilterOptions] = useState({ tipo_espacio: [], tamano: [], zona: [], provincia: [], estado: [] });
  const [selectedSpaceOnMap, setSelectedSpaceOnMap] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [filterByMap, setFilterByMap] = useState(false);
  const [mapBounds, setMapBounds] = useState(null);
  const [activeTab, setActiveTab] = useState('exploracion');
  // Share modal
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [shareName, setShareName] = useState('');
  const [shareExpiresAt, setShareExpiresAt] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  // Links management
  const [sharedLinks, setSharedLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  // CRUD Modal
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingSpace, setEditingSpace] = useState(null);
  // Proposal Cart
  const [proposalCart, setProposalCart] = useState(new Set());
  const [mapsApiKey, setMapsApiKey] = useState(null);

  const handleToggleCart = useCallback((codigo) => {
    setProposalCart(prev => {
      const next = new Set(prev);
      if (next.has(codigo)) next.delete(codigo);
      else next.add(codigo);
      return next;
    });
  }, []);

  const { logout, currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const navigate = useNavigate();
  const mapContainerRef = useRef(null);

  /* ---- Auth ---- */
  const handleLogout = async () => {
    try { await logout(); navigate('/login'); } catch (e) { console.error(e); }
  };

  /* ---- Share ---- */
  const handleShare = async () => {
    setShareModalOpen(true);
    setShareLink('');
    setShareName('');
    setShareExpiresAt('');
  };

  const generateShareLink = async () => {
    try {
      // Prioritize manual selections (proposalCart) if they exist
      const requestFilters = { ...filters };
      if (proposalCart.size > 0) {
        requestFilters.codigo = Array.from(proposalCart);
      }

      const payload = { filters: requestFilters, name: shareName || 'Sin nombre' };
      if (shareExpiresAt) {
        payload.expires_at = new Date(shareExpiresAt).toISOString();
      }
      const response = await axios.post(SHARE_API, payload);
      const { share_id } = response.data;
      const link = `${window.location.origin}/view/${share_id}`;
      setShareLink(link);
    } catch (e) {
      console.error(e);
      alert('No se pudo generar el enlace.');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text || shareLink);
    setSnackbarOpen(true);
  };

  // Pagination state
  const [inventoryPage, setInventoryPage] = useState(1);
  const [hasMoreInventory, setHasMoreInventory] = useState(true);
  const [loadingMoreInventory, setLoadingMoreInventory] = useState(false);

  const [linksPage, setLinksPage] = useState(1);
  const [hasMoreLinks, setHasMoreLinks] = useState(true);
  const [loadingMoreLinks, setLoadingMoreLinks] = useState(false);
  const [filterLinksCreatedBy, setFilterLinksCreatedBy] = useState('');
  const [allUsers, setAllUsers] = useState([]);

  const LIMIT = 50;

  /* ---- Links management ---- */
  const fetchSharedLinks = useCallback(async (isLoadMore = false) => {
    if (isLoadMore) setLoadingMoreLinks(true);
    else setLoadingLinks(true);

    try {
      const nextPage = isLoadMore ? linksPage + 1 : 1;
      let url = `${SHARE_API}?page=${nextPage}&limit=${LIMIT}`;
      if (filterLinksCreatedBy) url += `&created_by=${filterLinksCreatedBy}`;
      
      const response = await axios.get(url);
      const data = response.data;

      if (isLoadMore) {
        setSharedLinks(prev => [...prev, ...data]);
        setLinksPage(nextPage);
      } else {
        setSharedLinks(data);
        setLinksPage(1);
      }
      setHasMoreLinks(data.length === LIMIT);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingLinks(false);
      setLoadingMoreLinks(false);
    }
  }, [linksPage, filterLinksCreatedBy]);

  useEffect(() => {
    if (activeTab === 'enlaces') {
      fetchSharedLinks(false);
      if (isAdmin && allUsers.length === 0) {
        axios.get(`${API_ROOT}/api/admin/users`)
          .then(res => setAllUsers(res.data))
          .catch(err => console.error("Error fetching users for links filter:", err));
      }
    }
  }, [activeTab, filterLinksCreatedBy, isAdmin]);

  /* ---- Data fetching (Inventory) ---- */
  const fetchData = useCallback(async (isLoadMore = false) => {
    if (isLoadMore) setLoadingMoreInventory(true);
    else setLoading(true);
    
    setError(null);
    try {
      const nextPage = isLoadMore ? inventoryPage + 1 : 1;
      const queryParams = new URLSearchParams();
      let isCodeFilterActive = false;

      // Add pagination params
      queryParams.append('page', nextPage);
      queryParams.append('limit', LIMIT);

      for (const key in filters) {
        const v = filters[key];
        if (Array.isArray(v) && v.length > 0) { queryParams.append(key, v.join(',')); }
        else if (typeof v === 'string' && v.trim() !== '') {
          queryParams.append(key, v.trim());
          if (key === 'codigo') isCodeFilterActive = true;
        }
      }

      const response = await axios.get(`${API_BASE_URL}?${queryParams.toString()}`);
      let data = response.data;

      if (filterByMap && mapBounds) {
        data = data.filter(s => {
          const lat = parseFloat(s.Latitud), lng = parseFloat(s.Longitud);
          return mapBounds.contains({ lat, lng });
        });
      }

      if (isLoadMore) {
        setEspacios(prev => [...prev, ...data]);
        setInventoryPage(nextPage);
      } else {
        setEspacios(data);
        setInventoryPage(1);
      }

      setHasMoreInventory(data.length === LIMIT);

      if (isCodeFilterActive && data.length > 0) setSelectedSpaceOnMap(data[0]);
      else if (!data || data.length === 0) {
        if (!isLoadMore) setSelectedSpaceOnMap(null);
      }
    } catch (e) {
      setError(e.message);
      setSelectedSpaceOnMap(null);
    } finally {
      setLoading(false);
      setLoadingMoreInventory(false);
    }
  }, [filters, filterByMap, mapBounds, inventoryPage]);

  const handleOpenForm = (space = null) => {
    setEditingSpace(space);
    setFormModalOpen(true);
  };

  const handleFormSaved = () => {
    fetchData();
    fetchFilterOptions();
  };

  const handleDeleteSpace = async (codigo) => {
    if (!window.confirm(`¿Estás seguro de eliminar el espacio ${codigo}?`)) return;
    try {
      await axios.delete(`${API_BASE_URL}/${codigo}`);
      fetchData();
    } catch (e) {
      alert(`Error al eliminar: ${e.response?.data?.detail || e.message}`);
    }
  };

  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await axios.get(API_BASE_URL);
      const allData = response.data;
      setFilterOptions({
        tipo_espacio: [...new Set(allData.map(i => i.Tipo_Espacio))].filter(Boolean).sort(),
        tamano: [...new Set(allData.map(i => i.Tamano))].filter(Boolean).sort(),
        zona: [...new Set(allData.map(i => i.Zona))].filter(Boolean).sort(),
        provincia: [...new Set(allData.map(i => i.Provincia))].filter(Boolean).sort(),
        estado: [...new Set(allData.map(i => i.Estado))].filter(Boolean).sort(),
      });
    } catch (e) { console.error(e); }
  }, []);

  const fetchMapsKey = useCallback(async () => {
    try {
      const response = await axios.get(`${API_ROOT}/api/config/maps-key`);
      setMapsApiKey(response.data.apiKey);
    } catch (e) {
      console.error("Error fetching Maps API Key:", e);
    }
  }, []);

  useEffect(() => { fetchFilterOptions(); }, [fetchFilterOptions]);
  useEffect(() => { fetchMapsKey(); }, [fetchMapsKey]);
  useEffect(() => {
    const h = setTimeout(() => fetchData(), 500);
    return () => clearTimeout(h);
  }, [filters, fetchData]);

  const handleFilterChange = (name, value) => setFilters(p => ({ ...p, [name]: value }));
  const handleClearFilters = () => { setFilters(initialFilters); setSelectedSpaceOnMap(null); };
  const handleSpaceSelect = useCallback((space) => setSelectedSpaceOnMap(space), []);
  const handleBoundsChange = useCallback((bounds) => setMapBounds(bounds), []);

  /* ---- PDF export ---- */
  const handleExportToPdf = async () => {
    setExportingPdf(true);
    try {
      let mapImage = null;
      if (mapContainerRef.current && activeTab === 'exploracion') {
        const canvas = await html2canvas(mapContainerRef.current, { useCORS: true, allowTaint: true, backgroundColor: '#FFF' });
        mapImage = canvas.toDataURL('image/png');
      }

      // Prioritize manual selections (proposalCart) if they exist
      const requestFilters = { ...filters };
      if (proposalCart.size > 0) {
        requestFilters.codigo = Array.from(proposalCart);
      }

      const response = await axios.post(PDF_GENERATOR_API_URL, { 
        filters: requestFilters, 
        mapImage, 
        title: proposalCart.size > 0 ? 'Reporte de Selecciones' : 'Informe de Espacios' 
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      const filename = proposalCart.size > 0 ? 'reporte-selecciones.pdf' : 'informe-espacios.pdf';
      const a = document.createElement('a');
      a.href = url; a.setAttribute('download', filename);
      document.body.appendChild(a); a.click(); a.parentNode.removeChild(a);
    } catch (e) {
      alert(`Error al generar PDF: ${e.message}`);
    } finally { setExportingPdf(false); }
  };

  /* ---- Render ---- */
  return (
    <ThemeProvider theme={brandTheme}>
      <CssBaseline />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/view/:shareId" element={<SharedViewPage />} />
        <Route path="/catalog" element={<PublicPortal />} />
        <Route element={<ProtectedRoute />}>
          <Route path="/" element={
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100vw', overflow: 'hidden' }}>

              {/* AppBar */}
              <AppBar position="static" color="primary" elevation={0}>
                <Toolbar>
                  <img src={Logo} alt="Billboard Hub Logo" style={{ height: '46px', marginRight: '14px' }} />
                  <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700, letterSpacing: '-0.01em' }}>
                    Sistema de Gestión de Vallas
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mr: 1 }}>
                      {currentUser?.displayName || currentUser?.email}
                    </Typography>
                    <Chip
                      label={`${espacios.length} vallas`}
                      size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 600 }}
                    />
                    <NotificationsMenu />
                    <IconButton color="inherit" onClick={handleLogout} title="Cerrar Sesión">
                      <LogoutIcon />
                    </IconButton>
                  </Box>
                </Toolbar>
              </AppBar>

              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} centered>
                  {(isAdmin || currentUser?.can_view_dashboard) && <Tab value="dashboard" icon={<DashboardIcon />} label="Dashboard" iconPosition="start" />}
                  <Tab value="exploracion" icon={<MapIcon />} label="Exploración" iconPosition="start" />
                  <Tab value="inventario" icon={<ListAltIcon />} label="Inventario" iconPosition="start" />
                  <Tab value="enlaces" icon={<LinkIcon />} label="Enlaces" iconPosition="start" />
                  <Tab value="propuestas" icon={<DescriptionIcon />} label="Propuestas" iconPosition="start" />
                  <Tab value="cronograma" icon={<CalendarMonthIcon />} label="Cronograma" iconPosition="start" />
                  <Tab value="contratos" icon={<AssignmentIcon />} label="Contratos" iconPosition="start" />
                  <Tab value="leads" icon={<FeedbackIcon />} label="Leads" iconPosition="start" />
                  {isAdmin && <Tab value="usuarios" icon={<PeopleIcon />} label="Usuarios" iconPosition="start" />}
                  {isAdmin && <Tab value="auditoria" icon={<SecurityIcon />} label="Auditoría" iconPosition="start" />}
                </Tabs>
              </Box>

              {/* Body */}
              <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>

                {/* Sidebar — for Exploración, Inventario, Propuestas & Cronograma */}
                {(activeTab === 'exploracion' || activeTab === 'inventario' || activeTab === 'propuestas' || activeTab === 'cronograma') && (
                  <Paper elevation={6} sx={{
                    width: { xs: '100%', md: '300px' }, p: 2.5, overflowY: 'auto',
                    flexShrink: 0, display: 'flex', flexDirection: 'column',
                    bgcolor: 'background.paper',
                    boxShadow: '2px 0 12px rgba(0,0,0,0.06)',
                  }}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <Button variant="outlined" color="primary" onClick={handleClearFilters} disabled={loading} sx={{ flex: 1 }}>
                        Limpiar
                      </Button>
                      <Button variant="outlined" color="secondary" onClick={handleShare} startIcon={<ShareIcon />} disabled={loading} sx={{ flex: 1 }}>
                        Compartir
                      </Button>
                    </Box>
                    <Button
                      variant="contained" color="primary" fullWidth
                      onClick={handleExportToPdf} startIcon={<PictureAsPdfIcon />}
                      disabled={exportingPdf || loading || (espacios.length === 0 && proposalCart.size === 0)}
                      sx={{ mb: 1 }}
                    >
                      {exportingPdf ? 'Exportando...' : 'Exportar PDF'}
                    </Button>

                    {proposalCart.size > 0 && (
                      <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        bgcolor: 'success.light', color: 'success.contrastText',
                        borderRadius: 1.5, px: 1.5, py: 0.75, mb: 1,
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CheckBoxIcon fontSize="small" />
                          <Typography variant="body2" fontWeight={700}>
                            {proposalCart.size} seleccionada{proposalCart.size > 1 ? 's' : ''}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          sx={{ color: 'inherit', minWidth: 'auto', textDecoration: 'underline', fontSize: '0.72rem' }}
                          onClick={() => setProposalCart(new Set())}
                        >
                          Limpiar
                        </Button>
                      </Box>
                    )}

                    <FilterPanel filters={filters} onFilterChange={handleFilterChange} filterOptions={filterOptions} />

                    <Box sx={{ mt: 1 }}>
                      <Button
                        size="small" fullWidth
                        variant={filterByMap ? 'contained' : 'outlined'}
                        onClick={() => setFilterByMap(v => !v)}
                        sx={{ fontSize: '0.72rem' }}
                      >
                        {filterByMap ? '✓ Filtrando por área del mapa' : 'Filtrar por área del mapa'}
                      </Button>
                    </Box>

                    <Box sx={{ my: 2, borderBottom: '1px solid', borderColor: 'divider' }} />
                    <StatsDashboard espacios={espacios} />
                    <Box sx={{ my: 2, borderBottom: '1px solid', borderColor: 'divider' }} />
                    <SpaceDetailsPanel
                      selectedSpace={selectedSpaceOnMap}
                      proposalCart={proposalCart}
                      onToggleCart={handleToggleCart}
                    />
                  </Paper>
                )}

                {/* Main content area */}
                <Box sx={{ flexGrow: 1, overflow: 'hidden', position: 'relative' }}>

                  {/* Tab: KPI Dashboard */}
                  {activeTab === 'dashboard' && (
                    <KpiDashboard espacios={espacios} />
                  )}

                  {/* Tab: Map (Exploración) */}
                  {activeTab === 'exploracion' && (
                    <Box sx={{ width: '100%', height: '100%', position: 'relative' }} ref={mapContainerRef}>
                      {loading && (
                        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 1000, bgcolor: 'rgba(255,255,255,0.9)', p: 3, borderRadius: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={32} />
                          <Typography variant="body2" color="text.secondary">Cargando datos...</Typography>
                        </Box>
                      )}
                      {error && (
                        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 1000, width: '80%', maxWidth: 400 }}>
                          <Alert severity="error" variant="filled">{error}</Alert>
                        </Box>
                      )}
                      {mapsApiKey ? (
                        <MapComponent
                          espacios={espacios}
                          onSpaceSelect={handleSpaceSelect}
                          selectedSpaceFromApp={selectedSpaceOnMap}
                          onBoundsChange={handleBoundsChange}
                          onToggleCart={handleToggleCart}
                          proposalCart={proposalCart}
                          mapsApiKey={mapsApiKey}
                        />
                      ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: 2 }}>
                          <CircularProgress size={30} />
                          <Typography variant="body2" color="text.secondary">Iniciando mapa...</Typography>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Tab: Inventory table */}
                  {activeTab === 'inventario' && (
                    <InventoryManager 
                      espacios={espacios}
                      onOpenForm={handleOpenForm}
                      onDelete={handleDeleteSpace}
                      onSelect={handleSpaceSelect}
                      onViewMap={() => setActiveTab('exploracion')}
                      hasMore={hasMoreInventory}
                      loadingMore={loadingMoreInventory}
                      onLoadMore={() => fetchData(true)}
                    />
                  )}

                  {/* Tab: Links manager */}
                  {activeTab === 'enlaces' && (
                    <Box sx={{ p: 3, height: '100%', overflowY: 'auto' }}>
                      <ShareLinksManager 
                        links={sharedLinks} 
                        loading={loadingLinks} 
                        onRefresh={() => fetchSharedLinks(false)} 
                        hasMore={hasMoreLinks}
                        loadingMore={loadingMoreLinks}
                        onLoadMore={() => fetchSharedLinks(true)}
                        filterCreatedBy={filterLinksCreatedBy}
                        setFilterCreatedBy={setFilterLinksCreatedBy}
                        users={allUsers}
                        isAdmin={isAdmin}
                      />
                    </Box>
                  )}

                  {/* Tab: Proposal builder */}
                  {activeTab === 'propuestas' && (
                    <ProposalBuilder
                      espacios={espacios}
                      mapContainerRef={mapContainerRef}
                      proposalCart={proposalCart}
                      setProposalCart={setProposalCart}
                      filters={filters}
                    />
                  )}

                  {/* Tab: Cronograma Gantt */}
                  {activeTab === 'cronograma' && (
                    <Box sx={{ flexGrow: 1, height: '100%', overflow: 'hidden' }}>
                      <InventoryGantt espacios={espacios} />
                    </Box>
                  )}

                  {/* Tab: Contratos manager */}
                  {activeTab === 'contratos' && (
                    <Box sx={{ flexGrow: 1, height: '100%', overflow: 'hidden', bgcolor: '#f4f6f8' }}>
                      <AsignacionesManager espacios={espacios} />
                    </Box>
                  )}

                  {/* Tab: Leads manager */}
                  {activeTab === 'leads' && (
                    <Box sx={{ p: 3, height: '100%', overflowY: 'auto' }}>
                      <LeadsManager />
                    </Box>
                  )}

                  {/* Tab: Users manager */}
                  {activeTab === 'usuarios' && isAdmin && (
                    <Box sx={{ flexGrow: 1, height: '100%', overflow: 'hidden' }}>
                      <UsersManager />
                    </Box>
                  )}

                  {/* Tab: Logs manager */}
                  {activeTab === 'auditoria' && isAdmin && (
                    <Box sx={{ flexGrow: 1, height: '100%', overflow: 'hidden' }}>
                      <LogsManager />
                    </Box>
                  )}

                </Box>
              </Box>
            </Box>
          } />
        </Route>
      </Routes>

      {/* Share Modal */}
      <Modal open={shareModalOpen} onClose={() => setShareModalOpen(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          width: 480, bgcolor: 'background.paper', borderRadius: 3, boxShadow: 24, p: 4
        }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>Crear enlace compartible</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Cualquiera con este enlace verá los espacios con los filtros actuales, sin necesidad de login.
          </Typography>
          <TextField
            label="Nombre del enlace (opcional)"
            fullWidth size="small" sx={{ mb: 2 }}
            value={shareName}
            onChange={e => setShareName(e.target.value)}
            placeholder="Ej: Cliente Empresa XYZ - Santo Domingo"
          />
          <TextField
            label="Fecha de vencimiento (opcional)"
            type="datetime-local"
            fullWidth size="small" sx={{ mb: 3 }}
            InputLabelProps={{ shrink: true }}
            value={shareExpiresAt}
            onChange={e => setShareExpiresAt(e.target.value)}
          />
          {!shareLink ? (
            <Button variant="contained" fullWidth onClick={generateShareLink} startIcon={<ShareIcon />}>
              Generar enlace
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField fullWidth size="small" value={shareLink} InputProps={{ readOnly: true }} />
              <Button variant="contained" onClick={() => copyToClipboard(shareLink)} startIcon={<ContentCopyIcon />}>
                Copiar
              </Button>
            </Box>
          )}
        </Box>
      </Modal>

      <Snackbar
        open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)}
        message="✓ Enlace copiado al portapapeles"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <BillboardFormModal
        open={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        onSaved={handleFormSaved}
        initialData={editingSpace}
      />
    </ThemeProvider>
  );
}

export default App;